/**
 * DH Slider — Universal carousel/slider Web Component.
 *
 * Usage:
 *   <dh-slider>
 *     <div data-slider-track>
 *       <div>Slide 1</div>
 *       <div>Slide 2</div>
 *     </div>
 *     <!-- Optional: prev/next buttons anywhere inside the element -->
 *     <button data-slider-prev aria-label="Previous">‹</button>
 *     <button data-slider-next aria-label="Next">›</button>
 *     <!-- Optional: declarative scrollbar -->
 *     <div data-slider-scrollbar>
 *       <div data-slider-scrollbar-thumb></div>
 *     </div>
 *   </dh-slider>
 *
 * Attributes:
 *   loop="true"        — wrap around at edges (for prev/next buttons)
 *   direction="vertical" — vertical slider (default: horizontal)
 */
class DHSlider extends HTMLElement {
  #track = null;
  #scrollbar = null;
  #scrollbarThumb = null;
  #prevBtn = null;
  #nextBtn = null;
  #isDragging = false;
  #startPos = 0;
  #startScroll = 0;
  #vertical = false;
  #resizeObserver = null;
  #mutationObserver = null;
  #handlers = {};

  connectedCallback() {
    if (!this.#init()) {
      this.#mutationObserver = new MutationObserver(() => {
        if (this.#init()) {
          this.#mutationObserver.disconnect();
          this.#mutationObserver = null;
        }
      });
      this.#mutationObserver.observe(this, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#unbindEvents();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  #init() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return false;

    this.#prevBtn = this.querySelector('[data-slider-prev]');
    this.#nextBtn = this.querySelector('[data-slider-next]');
    this.#scrollbar = this.querySelector('[data-slider-scrollbar]');
    this.#scrollbarThumb = this.querySelector('[data-slider-scrollbar-thumb]');

    this.#vertical = this.getAttribute('direction') === 'vertical';

    this.#bindEvents();
    this.#updateScrollbar();
    this.#updateButtons();
    return true;
  }

  get loop() {
    return this.getAttribute('loop') === 'true';
  }

  // --- Axis helpers ---

  get #scrollPos() {
    return this.#vertical ? this.#track.scrollTop : this.#track.scrollLeft;
  }

  get #scrollSize() {
    return this.#vertical ? this.#track.scrollHeight : this.#track.scrollWidth;
  }

  get #clientSize() {
    return this.#vertical ? this.#track.clientHeight : this.#track.clientWidth;
  }

  get #maxScroll() {
    return this.#scrollSize - this.#clientSize;
  }

  // --- Scrollbar ---

  #updateScrollbar() {
    if (!this.#scrollbar || !this.#scrollbarThumb) return;

    if (this.#scrollSize <= this.#clientSize) {
      this.#scrollbar.hidden = true;
      return;
    }
    this.#scrollbar.hidden = false;

    const barSize = this.#vertical ? this.#scrollbar.clientHeight : this.#scrollbar.clientWidth;
    const thumbSize = Math.max((this.#clientSize / this.#scrollSize) * barSize, 40);
    const maxThumbPos = barSize - thumbSize;
    const thumbPos = (this.#scrollPos / this.#maxScroll) * maxThumbPos;

    const sizeProp = this.#vertical ? 'height' : 'width';
    const otherProp = this.#vertical ? 'width' : 'height';
    const translate = this.#vertical ? `translateY(${thumbPos}px)` : `translateX(${thumbPos}px)`;

    this.#scrollbarThumb.style[sizeProp] = `${thumbSize}px`;
    this.#scrollbarThumb.style[otherProp] = '';
    this.#scrollbarThumb.style.transform = translate;
  }

  // --- Navigation ---

  #getScrollStep() {
    const slides = Array.from(this.#track.children);
    if (slides.length === 0) return this.#clientSize;

    const first = slides[0];
    const style = getComputedStyle(this.#track);
    const gap = parseFloat(style.columnGap) || parseFloat(style.rowGap) || parseFloat(style.gap) || 0;

    return (this.#vertical ? first.offsetHeight : first.offsetWidth) + gap;
  }

  scrollPrev() {
    const step = this.#getScrollStep();
    const prop = this.#vertical ? 'top' : 'left';

    if (this.#scrollPos <= 0 && this.loop) {
      this.#track.scrollTo({ [prop]: this.#maxScroll, behavior: 'smooth' });
    } else {
      this.#track.scrollBy({ [prop]: -step, behavior: 'smooth' });
    }
  }

  scrollNext() {
    const step = this.#getScrollStep();
    const prop = this.#vertical ? 'top' : 'left';

    if (this.#scrollPos >= this.#maxScroll - 1 && this.loop) {
      this.#track.scrollTo({ [prop]: 0, behavior: 'smooth' });
    } else {
      this.#track.scrollBy({ [prop]: step, behavior: 'smooth' });
    }
  }

  #updateButtons() {
    if (!this.#prevBtn && !this.#nextBtn) return;

    const isScrollable = this.#scrollSize > this.#clientSize;

    console.log(this);
    console.log(isScrollable);

    if (this.#prevBtn) {
      if (!isScrollable) {
        this.#prevBtn.hidden = true;
      } else {
        this.#prevBtn.hidden = false;
        this.#prevBtn.disabled = !this.loop && this.#scrollPos <= 0;
      }
    }

    if (this.#nextBtn) {
      if (!isScrollable) {
        this.#nextBtn.hidden = true;
      } else {
        this.#nextBtn.hidden = false;
        this.#nextBtn.disabled = !this.loop && this.#scrollPos >= this.#maxScroll - 1;
      }
    }
  }

  // --- Events ---

  #bindEvents() {
    const h = this.#handlers;

    h.onScroll = () => {
      this.#updateScrollbar();
      this.#updateButtons();
    };
    this.#track.addEventListener('scroll', h.onScroll, { passive: true });

    if (this.#prevBtn) {
      h.onPrev = () => this.scrollPrev();
      this.#prevBtn.addEventListener('click', h.onPrev);
    }
    if (this.#nextBtn) {
      h.onNext = () => this.scrollNext();
      this.#nextBtn.addEventListener('click', h.onNext);
    }

    h.onKeydown = (e) => this.#onKeydown(e);
    this.addEventListener('keydown', h.onKeydown);

    if (this.#scrollbarThumb) {
      h.onDragStart = (e) => this.#onDragStart(e);
      h.onDragMove = (e) => this.#onDragMove(e);
      h.onDragEnd = () => this.#onDragEnd();
      h.onScrollbarClick = (e) => this.#onScrollbarClick(e);

      this.#scrollbarThumb.addEventListener('mousedown', h.onDragStart);
      this.#scrollbarThumb.addEventListener('touchstart', h.onDragStart, { passive: false });
      this.#scrollbar.addEventListener('click', h.onScrollbarClick);
    }

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateScrollbar();
      this.#updateButtons();
    });
    this.#resizeObserver.observe(this.#track);
  }

  #unbindEvents() {
    const h = this.#handlers;

    if (this.#track && h.onScroll) {
      this.#track.removeEventListener('scroll', h.onScroll);
    }
    if (this.#prevBtn && h.onPrev) {
      this.#prevBtn.removeEventListener('click', h.onPrev);
    }
    if (this.#nextBtn && h.onNext) {
      this.#nextBtn.removeEventListener('click', h.onNext);
    }
    if (h.onKeydown) {
      this.removeEventListener('keydown', h.onKeydown);
    }
    if (this.#scrollbarThumb && h.onDragStart) {
      this.#scrollbarThumb.removeEventListener('mousedown', h.onDragStart);
      this.#scrollbarThumb.removeEventListener('touchstart', h.onDragStart);
    }
    if (this.#scrollbar && h.onScrollbarClick) {
      this.#scrollbar.removeEventListener('click', h.onScrollbarClick);
    }

    this.#removeDragListeners();
    this.#handlers = {};
  }

  #addDragListeners() {
    const h = this.#handlers;
    document.addEventListener('mousemove', h.onDragMove);
    document.addEventListener('mouseup', h.onDragEnd);
    document.addEventListener('touchmove', h.onDragMove, { passive: false });
    document.addEventListener('touchend', h.onDragEnd);
  }

  #removeDragListeners() {
    const h = this.#handlers;
    if (h.onDragMove) {
      document.removeEventListener('mousemove', h.onDragMove);
      document.removeEventListener('touchmove', h.onDragMove);
    }
    if (h.onDragEnd) {
      document.removeEventListener('mouseup', h.onDragEnd);
      document.removeEventListener('touchend', h.onDragEnd);
    }
  }

  // --- Drag handlers ---

  #onDragStart(e) {
    this.#isDragging = true;
    this.#scrollbarThumb.setAttribute('data-dragging', '');

    const pos = e.touches ? e.touches[0] : e;
    this.#startPos = this.#vertical ? pos.clientY : pos.clientX;
    this.#startScroll = this.#scrollPos;

    e.preventDefault();
    this.#addDragListeners();
  }

  #onDragMove(e) {
    if (!this.#isDragging) return;

    const pos = e.touches ? e.touches[0] : e;
    const delta = (this.#vertical ? pos.clientY : pos.clientX) - this.#startPos;

    const barSize = this.#vertical ? this.#scrollbar.clientHeight : this.#scrollbar.clientWidth;
    const thumbSize = this.#vertical ? this.#scrollbarThumb.clientHeight : this.#scrollbarThumb.clientWidth;
    const maxThumbPos = barSize - thumbSize;

    const scrollRatio = this.#maxScroll / maxThumbPos;
    if (this.#vertical) {
      this.#track.scrollTop = this.#startScroll + delta * scrollRatio;
    } else {
      this.#track.scrollLeft = this.#startScroll + delta * scrollRatio;
    }

    e.preventDefault();
  }

  #onDragEnd() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#scrollbarThumb.removeAttribute('data-dragging');
    this.#removeDragListeners();
  }

  #onScrollbarClick(e) {
    if (e.target === this.#scrollbarThumb) return;

    const scrollbarRect = this.#scrollbar.getBoundingClientRect();
    const clickPos = this.#vertical ? e.clientY - scrollbarRect.top : e.clientX - scrollbarRect.left;
    const barSize = this.#vertical ? this.#scrollbar.clientHeight : this.#scrollbar.clientWidth;
    const thumbSize = this.#vertical ? this.#scrollbarThumb.clientHeight : this.#scrollbarThumb.clientWidth;

    const targetScroll = ((clickPos - thumbSize / 2) / (barSize - thumbSize)) * this.#maxScroll;
    const prop = this.#vertical ? 'top' : 'left';

    this.#track.scrollTo({
      [prop]: Math.max(0, Math.min(targetScroll, this.#maxScroll)),
      behavior: 'smooth',
    });
  }

  // --- Keyboard ---

  #onKeydown(e) {
    const prevKeys = this.#vertical ? ['ArrowUp'] : ['ArrowLeft'];
    const nextKeys = this.#vertical ? ['ArrowDown'] : ['ArrowRight'];

    if (prevKeys.includes(e.key)) {
      e.preventDefault();
      this.scrollPrev();
    } else if (nextKeys.includes(e.key)) {
      e.preventDefault();
      this.scrollNext();
    }
  }
}

customElements.define('dh-slider', DHSlider);
